import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
} from "@mui/material";
import type {Opportunity} from "../briefingTypes";
import {BRIEFING} from "../briefingData";
import {useBriefing} from "../briefingState";

type Props = {
    opportunity: Opportunity
    open: boolean
    onClose: () => void
    onDismissed?: () => void
};

/**
 * Dismiss asks exactly one question and cannot complete without a reason —
 * the most valuable signal the readiness gate receives.
 */
export const DismissDialog: React.FC<Props> = ({opportunity, open, onClose, onDismissed}) => {
    const {dispatch} = useBriefing();
    const [reason, setReason] = React.useState<string>('');
    const [note, setNote] = React.useState('');

    const options = BRIEFING.dismiss_reasons_model.options;
    const needsNote = reason === 'wrong_read';

    const confirm = () => {
        if (!reason) return;
        const label = options.find(o => o.id === reason)?.label ?? reason;
        dispatch({type: 'dismiss', opportunityId: opportunity.id, reason: label, note: needsNote ? note.trim() || undefined : undefined});
        onClose();
        onDismissed?.();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>Why dismiss this opportunity?</DialogTitle>
            <DialogContent>
                <div className="db-dismiss-headline">{opportunity.title}</div>
                <RadioGroup value={reason} onChange={e => setReason(e.target.value)}>
                    {options.map(o =>
                        <FormControlLabel
                            key={o.id}
                            value={o.id}
                            control={<Radio size="small"/>}
                            label={
                                <span className="db-dismiss-option">
                                    <span className="db-dismiss-label">{o.label}</span>
                                    <span className="db-dismiss-effect">{o.effect}</span>
                                </span>
                            }
                        />
                    )}
                </RadioGroup>
                {needsNote &&
                    <TextField
                        size="small"
                        fullWidth
                        label="One-line note"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        sx={{mt: 1}}
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={!reason} onClick={confirm}>
                    {opportunity.actions.dismiss.label}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
